/**
 * Shared UI component for UI Stock Card List.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, ElementRef, input, output, signal, viewChildren } from '@angular/core';

import { UiBadgeComponent } from '../ui-badge/ui-badge.component';
import { UiBadgeTone } from '../ui-badge/ui-badge.types';
import { UiButtonComponent } from '../ui-button/ui-button.component';
import { UiCardComponent } from '../ui-card/ui-card.component';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

export interface UiStockCardListVariantItem {
  id: string;
  categoryId: string;
  productId: string;
  name: string;
  quantity: number;
  statusLabel?: string;
  status?: 'ok' | 'low' | 'out-of-stock';
}

export interface UiStockCardListProductItem {
  id: string;
  name: string;
  threshold?: number;
  totalQuantity?: number;
  statusLabel?: string;
  status?: 'ok' | 'low' | 'out-of-stock';
  isCashProduct?: boolean;
  variants: UiStockCardListVariantItem[];
}

export interface UiStockCardListCategoryItem {
  id: string;
  name: string;
  products: UiStockCardListProductItem[];
}

@Component({
  selector: 'ui-stock-card-list',
  standalone: true,
  imports: [UiBadgeComponent, UiButtonComponent, UiCardComponent, UiLabelTranslatePipe],
  templateUrl: './ui-stock-card-list.component.html',
  styleUrl: './ui-stock-card-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiStockCardListComponent {
  private readonly productRailRefs = viewChildren<ElementRef<HTMLElement>>('productRail');

  readonly items = input<UiStockCardListCategoryItem[]>([]);
  readonly emptyMessage = input('No stock items yet.');
  readonly showCategoryEdit = input(true);
  readonly showCategoryRemove = input(true);
  readonly showProductStatus = input(false);
  readonly showVariantStatus = input(true);
  readonly categoryEditRequested = output<UiStockCardListCategoryItem>();
  readonly categoryRemoveRequested = output<UiStockCardListCategoryItem>();
  readonly expandedCategoryIds = signal<string[]>([]);

  badgeTone(status: 'ok' | 'low' | 'out-of-stock'): UiBadgeTone {
    if (status === 'out-of-stock') {
      return 'churned';
    }

    if (status === 'low') {
      return 'overdue';
    }

    return 'active';
  }

  requestCategoryEdit(item: UiStockCardListCategoryItem): void {
    this.categoryEditRequested.emit(item);
  }

  requestCategoryRemove(item: UiStockCardListCategoryItem): void {
    this.categoryRemoveRequested.emit(item);
  }

  toggleCategory(categoryId: string): void {
    this.expandedCategoryIds.update((ids) => (ids.includes(categoryId) ? [] : [categoryId]));
  }

  isExpanded(categoryId: string): boolean {
    return this.expandedCategoryIds().includes(categoryId);
  }

  scrollProducts(categoryId: string, direction: 'previous' | 'next'): void {
    const rail = this.productRailRefs().find((ref) => ref.nativeElement.dataset['categoryId'] === categoryId)?.nativeElement;

    if (!rail) {
      return;
    }

    const firstCard = rail.querySelector<HTMLElement>('.ui-stock-card-list__product');
    const gap = Number.parseFloat(window.getComputedStyle(rail).columnGap || window.getComputedStyle(rail).gap || '0') || 0;
    const distance = (firstCard?.offsetWidth ?? 320) + gap;
    rail.scrollBy({ left: direction === 'next' ? distance : -distance, behavior: 'smooth' });
  }

  categoryProductCount(category: UiStockCardListCategoryItem): number {
    return category.products.length;
  }

  categoryVariantCount(category: UiStockCardListCategoryItem): number {
    return category.products.reduce((sum, product) => sum + product.variants.length, 0);
  }

  categoryTotalQuantity(category: UiStockCardListCategoryItem): number {
    return category.products.reduce(
      (sum, product) => sum + (product.totalQuantity ?? product.variants.reduce((variantSum, variant) => variantSum + variant.quantity, 0)),
      0
    );
  }
}
